import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, X, Trash2, Plus, Minus } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import { getPriceData } from '../utils/currency';

const StickyCart = () => {
    const [isOpen, setIsOpen] = useState(false);
    const { cart, itemCount, removeFromCart, updateQuantity } = useCart();

    // Get cart items (handle both array and object with items property)
    const cartItems = Array.isArray(cart) ? cart : (cart?.items || []);
    const cartTotal = cart?.total || cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

    if (itemCount === 0) return null;

    return (
        <>
            {/* Sticky Cart Button */}
            <button
                onClick={() => setIsOpen(true)}
                className="fixed bottom-24 left-6 z-[90] bg-gradient-to-r from-amber-500 to-orange-500 text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-transform group"
                data-testid="sticky-cart-btn"
            >
                <ShoppingCart className="h-6 w-6" />
                <span className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 text-white text-sm font-bold rounded-full flex items-center justify-center shadow-lg animate-bounce">
                    {itemCount}
                </span>
                
                {/* Tooltip */}
                <span className="absolute left-full ml-3 px-3 py-1.5 bg-slate-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    {getPriceData(cartTotal).eurFormatted} €
                </span>
            </button>

            {/* Cart Preview Slide-out */}
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <div 
                        className="fixed inset-0 bg-black/50 z-[95]"
                        onClick={() => setIsOpen(false)}
                    />
                    
                    {/* Panel */}
                    <div className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl z-[96] animate-in slide-in-from-right duration-300">
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b border-amber-100 bg-gradient-to-r from-amber-50 to-orange-50">
                            <div className="flex items-center gap-3">
                                <ShoppingCart className="h-6 w-6 text-amber-600" />
                                <h3 className="font-serif text-xl font-bold text-slate-800">Количка</h3>
                                <span className="px-2 py-1 bg-amber-500 text-white text-xs font-bold rounded-full">
                                    {itemCount}
                                </span>
                            </div>
                            <button 
                                onClick={() => setIsOpen(false)}
                                className="p-2 hover:bg-amber-100 rounded-full transition-colors"
                            >
                                <X className="h-5 w-5 text-slate-600" />
                            </button>
                        </div>

                        {/* Cart Items */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ maxHeight: 'calc(100vh - 200px)' }}>
                            {cartItems.map((item) => (
                                <div key={item.product_id} className="flex gap-4 bg-amber-50/50 p-3 rounded-xl">
                                    <img 
                                        src={item.image_url} 
                                        alt={item.name}
                                        className="w-20 h-20 object-cover rounded-lg"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <h4 className="font-medium text-slate-800 truncate">{item.name}</h4>
                                        <p className="text-amber-600 font-bold">{getPriceData(item.price).eurFormatted} €</p>
                                        
                                        {/* Quantity controls */}
                                        <div className="flex items-center gap-2 mt-2">
                                            <button 
                                                onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                                                className="w-7 h-7 flex items-center justify-center bg-white rounded-lg border border-amber-200 hover:bg-amber-100"
                                            >
                                                <Minus className="h-3 w-3" />
                                            </button>
                                            <span className="w-8 text-center font-medium">{item.quantity}</span>
                                            <button 
                                                onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                                                className="w-7 h-7 flex items-center justify-center bg-white rounded-lg border border-amber-200 hover:bg-amber-100"
                                            >
                                                <Plus className="h-3 w-3" />
                                            </button>
                                            <button 
                                                onClick={() => removeFromCart(item.product_id)}
                                                className="ml-auto p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Footer */}
                        <div className="p-4 border-t border-amber-100 bg-white">
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-slate-600">Общо:</span>
                                <div className="text-right">
                                    <span className="text-2xl font-bold text-amber-700">{getPriceData(cartTotal).eurFormatted} €</span>
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <Link to="/cart" className="flex-1" onClick={() => setIsOpen(false)}>
                                    <Button variant="outline" className="w-full border-amber-300 text-amber-700 hover:bg-amber-50">
                                        Виж количка
                                    </Button>
                                </Link>
                                <Link to="/checkout" className="flex-1" onClick={() => setIsOpen(false)}>
                                    <Button className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600">
                                        Поръчай
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default StickyCart;
